import React, { useState, useRef } from 'react';
import ReactCrop, { centerCrop, makeAspectCrop } from 'react-image-crop';
import imageCompression from 'browser-image-compression';
import 'react-image-crop/dist/ReactCrop.css';

const DevPage = () => {
// Store previews for each unique field name
  const [previews, setPreviews] = useState({
    banner169: null,
    squareAvatar: null
  });

  // Modal & Cropper state
  const [isEditing, setIsEditing] = useState(false);
  const [activeField, setActiveField] = useState(null); // 'banner169' or 'squareAvatar'
  const [imgSrc, setImgSrc] = useState('');
  const [aspect, setAspect] = useState(16 / 9);
  const [crop, setCrop] = useState();
  const [completedCrop, setCompletedCrop] = useState(null);

  const imgRef = useRef(null);

  // 1. Handle file selection from ANY input
  const onSelectFile = (e, fieldName, targetAspect) => {
    if (e.target.files && e.target.files.length > 0) {
      setActiveField(fieldName);
      setAspect(targetAspect); // Automatically set required ratio for this input
      setCrop(undefined);

      const reader = new FileReader();
      reader.addEventListener('load', () => {
        setImgSrc(reader.result?.toString() || '');
        setIsEditing(true); // Open the shared modal
      });
      reader.readAsDataURL(e.target.files[0]);
    }
  };

  const onImageLoad = (e) => {
    const { width, height } = e.currentTarget;
    const initialCrop = centerCrop(
      makeAspectCrop({ unit: '%', width: 90 }, aspect, width, height),
      width,
      height
    );
    setCrop(initialCrop);
  };

  // 2. Save crop and assign result back to the correct activeField
  const handleSaveCrop = async () => {
    if (!imgRef.current || !completedCrop || !activeField) return;

    const image = imgRef.current;
    const canvas = document.createElement('canvas');
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    canvas.width = completedCrop.width * scaleX;
    canvas.height = completedCrop.height * scaleY;
    const ctx = canvas.getContext('2d');

    ctx.drawImage(
      image,
      completedCrop.x * scaleX, completedCrop.y * scaleY,
      completedCrop.width * scaleX, completedCrop.height * scaleY,
      0, 0, canvas.width, canvas.height
    );

    canvas.toBlob(async (blob) => {
      if (!blob) return;
      const croppedFile = new File([blob], `${activeField}.webp`, { type: 'image/webp' });
      
      await compressAndFinalize(croppedFile, activeField);
      setIsEditing(false); // Close modal
    }, 'image/webp', 0.9);
  };

  const compressAndFinalize = async (file, fieldName) => {
    const options = {
      maxSizeMB: 0.3,
      maxWidthOrHeight: 1200,
      useWebWorker: true,
      fileType: 'image/webp',
    };

    try {
      const compressedFile = await imageCompression(file, options);
      const previewUrl = URL.createObjectURL(compressedFile);

      // Update only the specific field's preview
      setPreviews((prev) => ({ ...prev, [fieldName]: previewUrl }));

      // TODO: Append `compressedFile` to your FormData upload handler based on `fieldName`
    } catch (error) {
      console.error('Compression failed:', error);
    }
  };

  return (
    <div style={{ padding: '20px', fontFamily: 'sans-serif' }}>
      <h3>Multi-Input Thumbnail Uploader</h3>

      {/* --- INPUT 1: 16:9 Banner --- */}
      <div style={{ marginBottom: '20px' }}>
        <label><b>16:9 Banner Image:</b></label><br />
        <input 
          type="file" 
          accept="image/*" 
          onChange={(e) => onSelectFile(e, 'banner169', 16 / 9)} 
        />
        {previews.banner169 && (
          <div style={{ position: 'relative', display: 'inline-block', marginTop: '10px' }}>
            <img src={previews.banner169} alt="16:9 Preview" style={{ width: '200px', borderRadius: '6px', display: 'block' }} />
            <button onClick={() => { setActiveField('banner169'); setAspect(16/9); setIsEditing(true); }} style={{ position: 'absolute', bottom: 5, right: 5, background: 'rgba(0,0,0,0.7)', color: '#fff', border: 'none', padding: '4px 8px', borderRadius: '4px', cursor: 'pointer' }}>
              Edit Crop
            </button>
          </div>
        )}
      </div>

      {/* --- INPUT 2: Square Avatar --- */}
      <div style={{ marginBottom: '20px' }}>
        <label><b>Square (1:1) Avatar Image:</b></label><br />
        <input 
          type="file" 
          accept="image/*" 
          onChange={(e) => onSelectFile(e, 'squareAvatar', 1)} 
        />
        {previews.squareAvatar && (
          <div style={{ position: 'relative', display: 'inline-block', marginTop: '10px' }}>
            <img src={previews.squareAvatar} alt="Square Preview" style={{ width: '150px', borderRadius: '6px', display: 'block' }} />
            <button onClick={() => { 
              setActiveField('squareAvatar'); 
              setAspect(1); 
              setIsEditing(true); }} style={{ position: 'absolute', bottom: 5, right: 5, background: 'rgba(0,0,0,0.7)', color: '#fff', border: 'none', padding: '4px 8px', borderRadius: '4px', cursor: 'pointer' }}>
              Edit Crop
            </button>
          </div>
        )}
      </div>

      {/* --- SHARED ABSOLUTE OVERLAY CROP MODAL --- */}
      {isEditing && (
        <div style={{
          position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
          background: 'rgba(0, 0, 0, 0.85)', zIndex: 9999,
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center'
        }}>
          <div style={{ background: '#fff', padding: '20px', borderRadius: '8px', maxWidth: '90vw', maxHeight: '90vh', overflow: 'auto' }}>
            
            <p style={{ margin: '0 0 10px 0', fontWeight: 'bold' }}>
              Editing: {activeField === 'banner169' ? '16:9 Banner' : 'Square Avatar'}
            </p>

            <ReactCrop
              crop={crop}
              onChange={(c) => setCrop(c)}
              onComplete={(c) => setCompletedCrop(c)}
              aspect={aspect}
            >
              <img 
                ref={imgRef} 
                src={imgSrc} 
                alt="Crop source" 
                onLoad={onImageLoad} 
                style={{ maxHeight: '60vh', display: 'block' }}
              />
            </ReactCrop>

            <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button onClick={() => setIsEditing(false)} style={{ padding: '8px 16px', background: '#ccc', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                Cancel
              </button>
              <button onClick={handleSaveCrop} style={{ padding: '8px 16px', background: '#28a745', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                Save Crop
              </button>
            </div>

          </div>
        </div>
      )}
    </div>
  );
}

export default DevPage;